import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ExternalLink, Pencil, Plus, Power, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button.jsx';
import { Badge } from '@/components/ui/badge.jsx';
import { Input } from '@/components/ui/input.jsx';
import { Switch } from '@/components/ui/switch.jsx';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog.jsx';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from '@/components/ui/alert-dialog.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import CrmContactPicker from '@/components/licenses/CrmContactPicker.jsx';
import { useCarrier } from '@/hooks/useCarriers.js';
import { updateCarrier, searchCarrierCrmContacts } from '@/services/carriers.service.js';
import { LINE_LABELS, ROUTING_LABELS } from '@/lib/carrier-labels.js';

// Carrier detail/edit. routing_type (and slug/api_handler/cobroker wiring) is
// structural — the PUT validator rejects it, so it renders read-only here.
// Activate/deactivate is the only "delete": carriers are never removed.

const KNOWN_LINES = ['hull_liability', 'hangar', 'property', 'general_liability'];

// NULL IS AN ANSWER THE UI HAS TO BE ABLE TO SHOW. A two-position Switch cannot
// hold "nobody has been asked", so it gets its own position. Rendering null as
// "off" is how a carrier nobody asked becomes a carrier that declined.
function triLabel(v) {
  return v === true ? 'Yes' : v === false ? 'No' : 'Not asked';
}

function TriStateFlag({ label, value, onChange, yes }) {
  const opts = [[true, 'Yes'], [false, 'No'], [null, 'Not asked']];
  return (
    <div>
      <FieldLabel>{label}</FieldLabel>
      <div className="flex h-11 items-center gap-1">
        {opts.map(([v, text]) => (
          <button
            key={String(v)} type="button" onClick={() => onChange(v)}
            className={`rounded-pill border px-3 py-1 text-xs ${value === v
              ? 'border-accent bg-accent-soft font-medium text-accent'
              : 'border-border text-text-muted hover:border-border-strong'}`}
          >{text}</button>
        ))}
      </div>
      {value === true && yes ? <p className="mt-0.5 text-[11px] text-text-dim">{yes}</p> : null}
    </div>
  );
}

function seedForm(row) {
  return {
    name: row.name || '',
    auto_send: !!row.auto_send,
    // THREE STATES, NOT TWO. `null` is "nobody has been asked" and must survive a
    // save of an unrelated field, so these are carried as null/true/false rather
    // than coerced with !!. A Switch cannot render the third state, so each pairs
    // with an explicit "Not asked" control below.
    requires_snkl: row.requires_snkl === null || row.requires_snkl === undefined ? null : !!row.requires_snkl,
    offers_short_term: row.offers_short_term === null || row.offers_short_term === undefined ? null : !!row.offers_short_term,
    priority: row.priority != null ? String(row.priority) : '0',
    // Night 12: the default commission, typed as a percent ("15"), stored as a fraction. Blank = none.
    default_commission_rate: row.default_commission_rate != null ? String(Math.round(Number(row.default_commission_rate) * 10000) / 100) : '',
    default_commission_share: row.default_commission_share != null ? String(Number(row.default_commission_share)) : '',
    co_brokered: row.co_brokered === true,
    nudge_days: row.nudge_days != null ? String(row.nudge_days) : '',
    notes: row.notes || '',
    portal_url: row.portal_url || '',
    lines: Array.isArray(row.lines_of_business) ? [...row.lines_of_business] : [],
    contacts: Array.isArray(row.contacts)
      ? row.contacts.map((c) => ({ ...c, routing: c.routing ? { ...c.routing } : undefined }))
      : [],
    // NULL and [] are different answers and the form must not flatten them.
    // `null` = nobody has been asked who sends this carrier's documents;
    // `[]` = asked, and there are none. Seeded as null so an untouched
    // carrier stays honestly unasked when the form round-trips.
    documentSenders: Array.isArray(row.document_senders)
      ? row.document_senders.map((s) => ({ ...s }))
      : null,
  };
}

export default function CarrierDetail() {
  const { slug } = useParams();
  const { row, error, loading, refresh } = useCarrier(slug);

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [contactDialog, setContactDialog] = useState(null); // { index: number|null }
  const [confirmActive, setConfirmActive] = useState(false);
  const [toggling, setToggling] = useState(false);

  useEffect(() => { if (row) setForm(seedForm(row)); }, [row]);

  if (loading && !row) return <Shell><div className="text-sm text-text-muted">Loading…</div></Shell>;
  if (error || !row || !form) return <Shell><div className="text-sm text-accent">Couldn't load this carrier.</div></Shell>;

  // Hangar carriers get the relaxed contact contract (no default flag, no CRM
  // link required) — mirrors the PUT route's isHangar guard exactly.
  const isHangar = Array.isArray(row.lines_of_business) && row.lines_of_business.includes('hangar');
  const isPortal = row.routing_type === 'portal';
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  function toggleLine(key) {
    setForm((p) => ({
      ...p,
      lines: p.lines.includes(key) ? p.lines.filter((l) => l !== key) : [...p.lines, key],
    }));
  }

  function validate() {
    const errs = [];
    if (!form.name.trim()) errs.push('Name is required');
    const p = Number(form.priority);
    if (!Number.isInteger(p) || p < 0) errs.push('Priority must be a non-negative integer');
    if (form.nudge_days.trim() !== '') {
      const n = Number(form.nudge_days);
      if (!Number.isInteger(n) || n < 1) errs.push('Follow-up after must be a positive whole number of days (or blank for the default)');
    }
    if (isPortal && !form.portal_url.trim()) errs.push('Portal URL is required for a portal carrier');
    if (form.default_commission_rate.trim() !== '') {
      const r = Number(form.default_commission_rate);
      if (!Number.isFinite(r) || r < 0 || r > 100) errs.push('Default commission rate is a percent between 0 and 100 (e.g. 15), or blank for none');
    }
    if (form.default_commission_share.trim() !== '') {
      const sh = Number(form.default_commission_share);
      if (!Number.isFinite(sh) || sh <= 0 || sh > 1) errs.push('Default share is a fraction above 0 and up to 1 (e.g. 0.5), or blank for none');
    }
    if (!isHangar && form.contacts.length > 0) {
      if (!form.contacts.some((c) => c.default === true)) {
        errs.push('At least one contact must be marked default');
      }
    }
    return errs;
  }

  function buildPatch() {
    const body = {
      name: form.name.trim(),
      auto_send: form.auto_send,
      priority: Number(form.priority),
      notes: form.notes.trim() === '' ? null : form.notes.trim(),
      lines_of_business: form.lines,
      contacts: form.contacts,
      nudge_days: form.nudge_days.trim() === '' ? null : Number(form.nudge_days),
      // Sent as-is including null: the validator accepts the third state and
      // flattening it to false here would record "asked, and they do not".
      requires_snkl: form.requires_snkl,
      offers_short_term: form.offers_short_term,
      default_commission_rate: form.default_commission_rate.trim() === '' ? null : Math.round(Number(form.default_commission_rate) * 100) / 10000,
      default_commission_share: form.default_commission_share.trim() === '' ? null : Number(form.default_commission_share),
      co_brokered: form.co_brokered,
    };
    if (isPortal) body.portal_url = form.portal_url.trim();
    // Only sent when the broker has actually answered. An untouched carrier
    // stays NULL rather than being written as an empty list, which would
    // record "there are none" on every save of an unrelated field.
    if (form.documentSenders !== null) body.document_senders = form.documentSenders;
    return body;
  }

  async function save() {
    const errs = validate();
    if (errs.length) { toast.error(errs[0]); return; }
    setSaving(true);
    try {
      await updateCarrier(slug, buildPatch());
      toast.success('Saved — dispatch picks this up immediately');
      setEditing(false);
      refresh();
    } catch (err) {
      toast.error(`Save failed: ${err.body?.errors?.[0] || err.body?.error || err.code || err.message}`);
    } finally { setSaving(false); }
  }

  async function toggleActive() {
    setToggling(true);
    try {
      await updateCarrier(slug, { active: !row.active });
      toast.success(row.active ? 'Carrier deactivated — removed from dispatch' : 'Carrier activated');
      setConfirmActive(false);
      refresh();
    } catch (err) {
      toast.error(`Update failed: ${err.body?.errors?.[0] || err.body?.error || err.code || err.message}`);
    } finally { setToggling(false); }
  }

  function saveContact(contact) {
    setForm((p) => {
      const next = [...p.contacts];
      if (contactDialog?.index == null) next.push(contact);
      else next[contactDialog.index] = contact;
      return { ...p, contacts: next };
    });
  }

  function removeContact(i) {
    // In-form only — nothing persists until Save, so Cancel undoes it.
    setForm((p) => ({ ...p, contacts: p.contacts.filter((_, idx) => idx !== i) }));
  }

  const contacts = editing ? form.contacts : (Array.isArray(row.contacts) ? row.contacts : []);
  const staticLines = Array.isArray(row.lines_of_business) ? row.lines_of_business : [];

  return (
    <Shell>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-semibold text-text">{row.name}</h2>
            <Badge variant={row.active ? 'success' : 'dashed'}>{row.active ? 'Active' : 'Inactive'}</Badge>
          </div>
          <p className="mt-1 text-sm text-text-muted">
            <span className="font-mono uppercase">{row.slug}</span>
            {' · '}{ROUTING_LABELS[row.routing_type] || row.routing_type}
            {row.is_cobroker ? ' · co-broker' : ''}
          </p>
        </div>
        {!editing
          ? <Button variant="outline" onClick={() => setEditing(true)}>Edit</Button>
          : <div className="flex gap-2">
              <Button variant="outline" onClick={() => { setEditing(false); setForm(seedForm(row)); }}>Cancel</Button>
              <Button disabled={saving} onClick={save}>{saving ? 'Saving…' : 'Save'}</Button>
            </div>}
      </div>

      {/* Fields */}
      <div className="mt-5 grid grid-cols-2 gap-4">
        <Row label="Name" editing={editing}><Input value={form.name} onChange={set('name')} /></Row>
        <Static label="Name" value={row.name} editing={editing} />

        <Row label="Priority (lower = first)" editing={editing}>
          <Input type="number" min="0" step="1" value={form.priority} onChange={set('priority')} />
        </Row>
        <Static label="Priority" value={String(row.priority ?? '—')} editing={editing} />

        {/* Nudge timer (MV-02 item 3): quiet days on a waiting market row
            before it derives nudge-due. Blank = the shop-wide default. */}
        <Row label="Follow-up after (days quiet)" editing={editing}>
          <Input type="number" min="1" step="1" placeholder="default" value={form.nudge_days} onChange={set('nudge_days')} />
        </Row>
        <Static label="Follow-up after" value={row.nudge_days != null ? `${row.nudge_days} days quiet` : 'Default'} editing={editing} />

        {editing
          ? <div>
              <FieldLabel>Auto-send</FieldLabel>
              <label className="flex h-11 items-center gap-2 text-sm text-text">
                <Switch checked={form.auto_send} onCheckedChange={(v) => setForm((p) => ({ ...p, auto_send: v }))} />
                Send without broker review
              </label>
            </div>
          : <Static label="Auto-send" value={row.auto_send ? 'Yes' : 'No'} editing={editing} />}

        {/* ── THE FLAGS DAVID COULD NOT REACH (Ruling 4, 2026-08-07) ────────
            His standing rule: "Switches should exist in TRACON not in Render."
            requires_snkl has been a column with a validator and no control since
            it shipped; offers_short_term is new and would have been the same.
            Both are here now, and offers_short_term is the ONLY thing that opens
            the six-month panel in the quote wizard.

            THREE STATES. A Switch has two, so "Not asked" is its own button —
            null is not a no, and a carrier nobody has asked must not read as one
            that declined. Same shape as the wizard's "— not answered —". */}
        {editing
          ? <TriStateFlag label="Writes short-term (6-month)" value={form.offers_short_term}
              onChange={(v) => setForm((p) => ({ ...p, offers_short_term: v }))}
              yes="Offers a six-month premium box on its quotes" />
          : <Static label="Writes short-term" value={triLabel(row.offers_short_term)} editing={editing} />}

        {editing
          ? <TriStateFlag label="Requires a SNKL" value={form.requires_snkl}
              onChange={(v) => setForm((p) => ({ ...p, requires_snkl: v }))}
              yes="Bundle a SNKL with this carrier's binds" />
          : <Static label="Requires a SNKL" value={triLabel(row.requires_snkl)} editing={editing} />}

        {/* Night 12: the carrier's default commission. Fills a collected row only where nothing is stated, labelled
            carrier default; the binder's rate or a stated dollar always wins. Plain inputs (no polish, David 2026-11-01). */}
        <Row label="Default commission rate (%)" editing={editing}><Input value={form.default_commission_rate} onChange={set('default_commission_rate')} inputMode="decimal" placeholder="blank = none" /></Row>
        <Static label="Default commission rate" value={row.default_commission_rate != null ? `${Math.round(Number(row.default_commission_rate) * 10000) / 100}%` : 'None'} editing={editing} />
        <Row label="Co-brokered (the wholesaler splits commission)" editing={editing}>
          <label className="flex h-11 items-center gap-2 text-sm text-text">
            <Switch checked={form.co_brokered} onCheckedChange={(v) => setForm((p) => ({ ...p, co_brokered: v }))} />
            Co-brokered
          </label>
        </Row>
        <Static label="Co-brokered" value={row.co_brokered ? 'Yes' : 'No'} editing={editing} />
        <Row label="Default share, co-brokered (e.g. 0.5)" editing={editing}><Input value={form.default_commission_share} onChange={set('default_commission_share')} inputMode="decimal" placeholder="blank = none" /></Row>
        <Static label="Default share" value={row.default_commission_share != null ? String(Number(row.default_commission_share)) : 'None'} editing={editing} />

        <Static label="Routing" value={`${ROUTING_LABELS[row.routing_type] || row.routing_type} (structural — change via migration)`} editing={false} />

        {isPortal && (
          <>
            <Row label="Portal URL" editing={editing}><Input value={form.portal_url} onChange={set('portal_url')} /></Row>
            <Static label="Portal" value={row.portal_url
              ? <a href={row.portal_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-accent">open portal <ExternalLink size={12} /></a>
              : '—'} editing={editing} />
          </>
        )}

        {editing
          ? <div className="col-span-2">
              <FieldLabel>Lines of business</FieldLabel>
              <div className="flex flex-wrap gap-3">
                {KNOWN_LINES.map((l) => (
                  <label key={l} className="flex items-center gap-2 text-sm text-text">
                    <input type="checkbox" checked={form.lines.includes(l)} onChange={() => toggleLine(l)} />
                    {LINE_LABELS[l]}
                  </label>
                ))}
              </div>
            </div>
          : <Static label="Lines of business" value={
              staticLines.length
                ? <span className="flex flex-wrap gap-1">{staticLines.map((l) => <Badge key={l} variant="outline">{LINE_LABELS[l] || l}</Badge>)}</span>
                : '—'} editing={editing} />}

        <Row label="Notes" editing={editing} full>
          <textarea rows={3} value={form.notes} onChange={set('notes')}
            className="w-full rounded-card border border-border bg-surface px-3 py-2 text-sm text-text focus:outline-none focus:ring-2 focus:ring-accent/40" />
        </Row>
        <Static label="Notes" value={row.notes || '—'} editing={editing} full />
      </div>

      {/* Contacts */}
      <div className="mt-6">
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-xs font-semibold uppercase tracking-[0.14em] text-text-muted">Contacts</h3>
          {editing && (
            <Button variant="outline" size="sm" onClick={() => setContactDialog({ index: null })}>
              <Plus size={14} strokeWidth={1.5} /> Add contact
            </Button>
          )}
        </div>
        {contacts.length === 0 ? (
          <div className="rounded-card border border-dashed border-border p-4 text-sm text-text-muted">
            {isPortal ? 'Portal carrier — submissions go through the portal, not email.' : 'No contacts yet.'}
          </div>
        ) : (
          <div className="space-y-2">
            {contacts.map((c, i) => (
              <div key={i} className="flex items-center justify-between gap-3 rounded-card border border-border bg-surface p-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="truncate text-sm font-medium text-text">{c.name}</span>
                    {c.default === true && <Badge variant="accent">Default</Badge>}
                    {c.routing?.type === 'effective_date_day' && (
                      <Badge variant="info">Effective day {c.routing.from}–{c.routing.to >= 31 ? 'EOM' : c.routing.to}</Badge>
                    )}
                  </div>
                  <div className="mt-0.5 truncate text-xs text-text-muted">
                    {c.email || '—'}{c.crm_contact_id ? ' · CRM linked' : ''}
                  </div>
                </div>
                {editing && (
                  <div className="flex shrink-0 gap-1">
                    <Button variant="ghost" size="icon" aria-label="Edit contact" onClick={() => setContactDialog({ index: i })}>
                      <Pencil size={15} strokeWidth={1.5} />
                    </Button>
                    <Button variant="ghost" size="icon" aria-label="Remove contact" onClick={() => removeContact(i)}>
                      <Trash2 size={15} strokeWidth={1.5} />
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        {editing && !isHangar && (
          <p className="mt-2 text-xs text-text-dim">Shop carrier: at least one contact must be marked default. Linking a CRM contact is optional.</p>
        )}
      </div>

      {/* Document senders: who this carrier DELIVERS from, which is not who
          we quote with. The Gmail watcher built its allowlist from Contacts
          above, so a delivery desk that never quotes was never watched and
          its policies never arrived. This is the door: a list David can edit
          without a deploy. */}
      <DocumentSenders
        editing={editing}
        value={editing ? form.documentSenders : (Array.isArray(row.document_senders) ? row.document_senders : null)}
        onChange={(next) => setForm((p) => ({ ...p, documentSenders: next }))}
      />

      {/* Activate / deactivate — the only "delete" this surface has */}
      <div className="mt-8 border-t border-border pt-4">
        <Button variant={row.active ? 'destructive' : 'outline'} onClick={() => setConfirmActive(true)}>
          <Power size={15} strokeWidth={1.5} /> {row.active ? 'Deactivate carrier' : 'Activate carrier'}
        </Button>
        <p className="mt-2 text-xs text-text-dim">
          Deactivating removes this carrier from dispatch and quoting panels. Carriers are never deleted — creation and removal are SQL-only.
        </p>
      </div>

      <OpwTermsCard row={row} slug={slug} onSaved={refresh} />

      <ContactDialog
        open={!!contactDialog}
        initial={contactDialog?.index != null ? form.contacts[contactDialog.index] : null}
        isHangar={isHangar}
        onOpenChange={(o) => { if (!o) setContactDialog(null); }}
        onSave={saveContact}
      />

      <AlertDialog open={confirmActive} onOpenChange={setConfirmActive}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{row.active ? 'Deactivate' : 'Activate'} {row.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              {row.active
                ? 'It stops appearing in dispatch and quoting panels immediately. Nothing is deleted — reactivate any time.'
                : 'It becomes available for dispatch and quoting immediately.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={toggling}>Back</AlertDialogCancel>
            <AlertDialogAction onClick={toggleActive} disabled={toggling}>
              {toggling ? 'Working…' : (row.active ? 'Deactivate' : 'Activate')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Shell>
  );
}

// ─── Open pilot warranty terms (David 2026-08-12, Two Roads To Allowed Use) ──
//
// The carrier's OPW terms, written to eligibility_rules.opw through the ONE
// carriers writer (updateCarrier → admin-carriers PATCH → validateCarrierUpdate).
// BLANK MEANS NO REQUIREMENT — the one place in TRACON where an empty field is
// a determination, so nothing here warns on blanks and nothing is seeded.
// A prose-only OPW returns MANUAL CHECK in any future comparison, never a pass.

const OPW_TYPE_CATEGORIES = [
  { value: 'retractable_gear', label: 'Retractable gear' },
  { value: 'multi_engine', label: 'Multi-engine' },
  { value: 'tailwheel', label: 'Tailwheel' },
  { value: 'sea', label: 'Seaplane' },
  { value: 'turboprop', label: 'Turboprop' },
  { value: 'turbofan_jet', label: 'Turbofan / jet' },
  { value: 'rotorwing', label: 'Rotorwing' },
];

function OpwTermsCard({ row, slug, onSaved }) {
  const stored = (row.eligibility_rules && row.eligibility_rules.opw) || {};
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [o, setO] = useState({
    certificate_min: stored.certificate_min || '',
    ratings: (stored.ratings || []).join(', '),
    total_hours: stored.total_hours != null ? String(stored.total_hours) : '',
    mm_hours: stored.mm_hours != null ? String(stored.mm_hours) : '',
    age_min: stored.age_min != null ? String(stored.age_min) : '',
    age_max: stored.age_max != null ? String(stored.age_max) : '',
    type_hours: stored.type_hours || [],
    prose: stored.prose || '',
  });
  const setK = (k) => (e) => setO((p) => ({ ...p, [k]: e.target.value }));

  function addTypeHour() {
    setO((p) => ({ ...p, type_hours: [...p.type_hours, { category: 'retractable_gear', hours: 0 }] }));
  }
  function setTypeHour(i, patch) {
    setO((p) => ({ ...p, type_hours: p.type_hours.map((t, j) => (j === i ? { ...t, ...patch } : t)) }));
  }
  function removeTypeHour(i) {
    setO((p) => ({ ...p, type_hours: p.type_hours.filter((_, j) => j !== i) }));
  }

  async function save() {
    // Blank = no requirement = key ABSENT, never 0 or ''.
    const opw = {};
    if (o.certificate_min.trim()) opw.certificate_min = o.certificate_min.trim();
    const ratings = o.ratings.split(',').map((s) => s.trim()).filter(Boolean);
    if (ratings.length) opw.ratings = ratings;
    for (const k of ['total_hours', 'mm_hours']) {
      if (String(o[k]).trim() !== '') {
        const n = Number(o[k]);
        if (!Number.isFinite(n) || n < 0) { toast.error(`${k.replace('_', ' ')} must be a non-negative number`); return; }
        opw[k] = n;
      }
    }
    for (const k of ['age_min', 'age_max']) {
      if (String(o[k]).trim() !== '') {
        const n = Number(o[k]);
        if (!Number.isInteger(n) || n < 14 || n > 120) { toast.error(`${k.replace('_', ' ')} must be a whole age`); return; }
        opw[k] = n;
      }
    }
    const th = o.type_hours
      .map((t) => ({ category: t.category, hours: Number(t.hours) }))
      .filter((t) => Number.isFinite(t.hours) && t.hours > 0);
    if (th.length) opw.type_hours = th;
    if (o.prose.trim()) opw.prose = o.prose.trim();

    setSaving(true);
    try {
      const rules = { ...(row.eligibility_rules || {}) };
      if (Object.keys(opw).length) rules.opw = opw; else delete rules.opw;
      await updateCarrier(slug, { eligibility_rules: rules });
      toast.success('OPW terms saved');
      setEditing(false);
      onSaved?.();
    } catch (err) {
      toast.error(`Save failed: ${err.body?.errors?.[0] || err.body?.error || err.code || err.message}`);
    } finally { setSaving(false); }
  }

  const fieldCls = 'w-full rounded-card border border-border bg-surface px-3 py-2 text-sm text-text focus:outline-none focus:ring-2 focus:ring-accent/40';
  const summary = [
    stored.certificate_min && `${stored.certificate_min} certificate`,
    stored.ratings?.length && `ratings: ${stored.ratings.join(', ')}`,
    stored.total_hours != null && `${stored.total_hours} TT`,
    stored.mm_hours != null && `${stored.mm_hours} in make & model`,
    stored.type_hours?.length && stored.type_hours.map((t) => `${t.hours} ${t.category.replace(/_/g, ' ')}`).join(', '),
    (stored.age_min != null || stored.age_max != null) && `age ${stored.age_min ?? 'any'}–${stored.age_max ?? 'any'}`,
    stored.prose && 'prose on file (manual check)',
  ].filter(Boolean).join(' · ');

  return (
    <div className="mt-6">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-text">Open pilot warranty terms</h3>
        {!editing
          ? <Button size="sm" variant="outline" onClick={() => setEditing(true)}>Edit</Button>
          : <div className="flex gap-2">
              <Button size="sm" variant="ghost" onClick={() => setEditing(false)} disabled={saving}>Cancel</Button>
              <Button size="sm" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save OPW terms'}</Button>
            </div>}
      </div>
      <p className="mb-3 text-xs text-text-muted">
        A blank term means NO requirement, which is normal (a fixed-gear single often states nothing
        but a certificate). These decide which pilots must go to this market, and what goes with them.
      </p>
      {!editing ? (
        <div className="rounded-card border border-border bg-bg/40 p-3 text-sm text-text">
          {summary || <span className="text-text-dim">No OPW terms configured.</span>}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3 rounded-card border border-border bg-bg/40 p-3">
          <div>
            <FieldLabel>Minimum certificate</FieldLabel>
            <input value={o.certificate_min} onChange={setK('certificate_min')} placeholder="e.g. Private" className={fieldCls} />
          </div>
          <div>
            <FieldLabel>Required ratings (comma-separated)</FieldLabel>
            <input value={o.ratings} onChange={setK('ratings')} placeholder="e.g. Instrument" className={fieldCls} />
          </div>
          <div>
            <FieldLabel>Total hours</FieldLabel>
            <input value={o.total_hours} onChange={setK('total_hours')} inputMode="numeric" placeholder="blank = none" className={fieldCls} />
          </div>
          <div>
            <FieldLabel>Make &amp; model hours</FieldLabel>
            <input value={o.mm_hours} onChange={setK('mm_hours')} inputMode="numeric" placeholder="blank = none" className={fieldCls} />
          </div>
          <div>
            <FieldLabel>Age minimum</FieldLabel>
            <input value={o.age_min} onChange={setK('age_min')} inputMode="numeric" placeholder="blank = none" className={fieldCls} />
          </div>
          <div>
            <FieldLabel>Age maximum</FieldLabel>
            <input value={o.age_max} onChange={setK('age_max')} inputMode="numeric" placeholder="blank = none" className={fieldCls} />
          </div>
          <div className="col-span-2">
            <div className="flex items-center justify-between">
              <FieldLabel>Category hours</FieldLabel>
              <Button size="sm" variant="ghost" onClick={addTypeHour}>+ Add</Button>
            </div>
            {o.type_hours.map((t, i) => (
              <div key={i} className="mt-1 flex items-center gap-2">
                <select value={t.category} onChange={(e) => setTypeHour(i, { category: e.target.value })} className={fieldCls}>
                  {OPW_TYPE_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
                <input value={t.hours} onChange={(e) => setTypeHour(i, { hours: e.target.value })} inputMode="numeric" className={fieldCls} />
                <Button size="sm" variant="ghost" onClick={() => removeTypeHour(i)}>Remove</Button>
              </div>
            ))}
          </div>
          <div className="col-span-2">
            <FieldLabel>Warranty prose (fallback)</FieldLabel>
            <textarea value={o.prose} onChange={setK('prose')} rows={3} className={fieldCls}
              placeholder="The carrier's own OPW wording. A prose-only requirement returns MANUAL CHECK in comparisons, never a pass." />
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Contact add/edit dialog ─────────────────────────────────────

const BLANK_CONTACT = { name: '', email: '', default: false, crm_contact_id: '', crm_name: '', routing: null };

// Who this carrier sends DOCUMENTS from: policy delivery desks, service
// mailboxes, whatever address the paperwork actually arrives from. Accepts a
// full address or a bare domain; the server validates both shapes and
// lowercases them, because the watcher matches on a lowercased from-address
// and a mixed-case entry would silently match nothing.
//
// THREE STATES, and the empty one is two different answers:
//   null  nobody has been asked          → "Not recorded", with a button
//   []    asked, and there are none      → "None", explicitly
//   [...] the registered senders
// Collapsing null into [] would make every unasked carrier look deliberately
// answered, which is the placeholder-spelled-like-an-answer defect this whole
// surface exists to stop.
function DocumentSenders({ editing, value, onChange }) {
  const [draft, setDraft] = useState('');
  const [label, setLabel] = useState('');
  const list = Array.isArray(value) ? value : [];
  const asked = value !== null && value !== undefined;

  function add() {
    const addr = draft.trim().toLowerCase();
    if (!addr) return;
    const looksValid = addr.includes('@')
      ? /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(addr)
      : /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/.test(addr);
    if (!looksValid) { toast.error('Enter a full email address or a bare domain (skywardinsurance.com)'); return; }
    if (list.some((s) => s.address === addr)) { toast.error('Already listed'); return; }
    onChange([...list, { address: addr, kind: addr.includes('@') ? 'email' : 'domain', ...(label.trim() ? { label: label.trim() } : {}) }]);
    setDraft(''); setLabel('');
  }

  return (
    <div className="mt-6">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-xs font-semibold uppercase tracking-[0.14em] text-text-muted">Document senders</h3>
        {editing && !asked && (
          <Button variant="outline" size="sm" onClick={() => onChange([])}>
            <Plus size={14} strokeWidth={1.5} /> Record senders
          </Button>
        )}
      </div>
      <p className="mb-2 text-xs text-text-dim">
        Addresses this carrier sends policies and endorsements from. Separate from Contacts, which is who we submit to.
        Anything listed here is watched for inbound documents.
      </p>

      {!asked ? (
        <div className="rounded-card border border-dashed border-border p-4 text-sm text-text-muted">
          Not recorded. Nobody has been asked who sends this carrier&apos;s documents.
        </div>
      ) : list.length === 0 ? (
        <div className="rounded-card border border-dashed border-border p-4 text-sm text-text-muted">
          None recorded for this carrier.
        </div>
      ) : (
        <div className="space-y-2">
          {list.map((s, i) => (
            <div key={s.address} className="flex items-center justify-between gap-3 rounded-card border border-border bg-surface p-3">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="truncate font-mono text-sm text-text">{s.address}</span>
                  <Badge variant="outline">{s.kind === 'domain' ? 'Whole domain' : 'Address'}</Badge>
                </div>
                {s.label ? <div className="mt-0.5 truncate text-xs text-text-muted">{s.label}</div> : null}
              </div>
              {editing && (
                <Button variant="ghost" size="icon" aria-label="Remove sender"
                  onClick={() => onChange(list.filter((_, idx) => idx !== i))}>
                  <Trash2 size={15} strokeWidth={1.5} />
                </Button>
              )}
            </div>
          ))}
        </div>
      )}

      {editing && asked && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <Input className="w-72" placeholder="avnsupport@carrier.com or carrier.com"
            value={draft} onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }} />
          <Input className="w-56" placeholder="What it is (optional)"
            value={label} onChange={(e) => setLabel(e.target.value)} />
          <Button variant="outline" size="sm" onClick={add}>
            <Plus size={14} strokeWidth={1.5} /> Add
          </Button>
        </div>
      )}
    </div>
  );
}

function draftFrom(initial) {
  if (!initial) return { ...BLANK_CONTACT };
  return {
    name: initial.name || '',
    email: initial.email || '',
    default: initial.default === true,
    crm_contact_id: initial.crm_contact_id || '',
    crm_name: initial.crm_name || initial.name || '',
    routing: initial.routing?.type === 'effective_date_day'
      ? { from: initial.routing.from, to: initial.routing.to }
      : null,
  };
}

function ContactDialog({ open, initial, isHangar, onOpenChange, onSave }) {
  const [d, setD] = useState(BLANK_CONTACT);
  useEffect(() => { if (open) setD(draftFrom(initial)); }, [open, initial]);

  const setDay = (k) => (e) => {
    const v = e.target.value === '' ? '' : parseInt(e.target.value, 10);
    setD((p) => ({ ...p, routing: { ...p.routing, [k]: v } }));
  };

  function commit() {
    const errs = [];
    if (!d.name.trim()) errs.push('Contact name is required');
    if (!d.email.trim() && !d.crm_contact_id) errs.push('Email (or a linked CRM contact) is required');
    if (d.routing) {
      const { from, to } = d.routing;
      if (!Number.isInteger(from) || !Number.isInteger(to) || from < 1 || to > 31 || from > to) {
        errs.push('Date window must use whole days 1–31 with from ≤ to');
      }
    }
    if (errs.length) { toast.error(errs[0]); return; }
    const c = { name: d.name.trim(), email: d.email.trim(), default: !!d.default };
    if (d.crm_contact_id) c.crm_contact_id = d.crm_contact_id;
    if (d.routing) c.routing = { type: 'effective_date_day', from: d.routing.from, to: d.routing.to };
    onSave(c);
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{initial ? 'Edit contact' : 'Add contact'}</DialogTitle>
          <DialogDescription>
            {isHangar
              ? 'Hangar contacts dispatch by email. The date window routes submissions by proposed-effective day of month.'
              : 'Shop contacts marked default must be linked to a CRM contact (the quote email handler needs it).'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <FieldLabel>Name</FieldLabel>
            <Input value={d.name} onChange={(e) => setD((p) => ({ ...p, name: e.target.value }))} />
          </div>
          <div>
            <FieldLabel>Email</FieldLabel>
            <Input type="email" value={d.email} onChange={(e) => setD((p) => ({ ...p, email: e.target.value }))} />
          </div>
          <div>
            <FieldLabel>CRM contact{isHangar ? ' (optional)' : ''}</FieldLabel>
            <CrmContactPicker
              value={d.crm_contact_id ? { id: d.crm_contact_id, name: d.crm_name || d.name || d.crm_contact_id } : null}
              searchFn={searchCarrierCrmContacts}
              placeholder="Search CRM contacts…"
              onSelect={(c) => setD((p) => c
                ? { ...p, crm_contact_id: c.id, crm_name: c.name || '', name: p.name || c.name || '', email: p.email || c.email || '' }
                : { ...p, crm_contact_id: '', crm_name: '' })}
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-text">
            <Switch checked={d.default} onCheckedChange={(v) => setD((p) => ({ ...p, default: v }))} />
            Default contact
          </label>

          <div className="rounded-card border border-border bg-bg/40 p-3">
            <label className="flex items-center gap-2 text-sm text-text">
              <Switch
                checked={!!d.routing}
                onCheckedChange={(v) => setD((p) => ({ ...p, routing: v ? { from: 1, to: 14 } : null }))}
              />
              Route by effective-date day
            </label>
            {d.routing && (
              <div className="mt-3 flex flex-wrap items-end gap-2">
                <div className="w-24">
                  <FieldLabel>From day</FieldLabel>
                  <Input type="number" min="1" max="31" value={d.routing.from} onChange={setDay('from')} />
                </div>
                <div className="w-24">
                  <FieldLabel>To day</FieldLabel>
                  <Input type="number" min="1" max="31" value={d.routing.to} onChange={setDay('to')} />
                </div>
                <div className="flex gap-1 pb-0.5">
                  {[{ label: '1–14', from: 1, to: 14 }, { label: '15–EOM', from: 15, to: 31 }].map((w) => (
                    <button key={w.label} type="button"
                      onClick={() => setD((p) => ({ ...p, routing: { from: w.from, to: w.to } }))}
                      className={['rounded-pill border px-2.5 py-1 text-xs',
                        d.routing.from === w.from && d.routing.to === w.to
                          ? 'border-accent/50 bg-accent-soft text-accent' : 'border-border text-text-dim'].join(' ')}>
                      {w.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={commit}>{initial ? 'Update contact' : 'Add contact'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Layout helpers (LicenseDetail pattern) ──────────────────────

function Shell({ children }) {
  const navigate = useNavigate();
  return (
    <div className="p-4">
      <button type="button" onClick={() => navigate(-1)} className="mb-3 flex items-center gap-1 text-sm text-text-muted hover:text-text">
        <ArrowLeft size={16} strokeWidth={1.5} /> Back
      </button>
      {children}
    </div>
  );
}

function FieldLabel({ children }) {
  return <label className="mb-1.5 block text-xs font-medium text-text-muted">{children}</label>;
}
// In edit mode show the input; in view mode the Row collapses (its Static sibling shows).
function Row({ label, editing, full, children }) {
  if (!editing) return null;
  return <div className={full ? 'col-span-2' : ''}><FieldLabel>{label}</FieldLabel>{children}</div>;
}
function Static({ label, value, editing, full }) {
  if (editing) return null;
  return (
    <div className={full ? 'col-span-2' : ''}>
      <div className="text-xs text-text-muted">{label}</div>
      <div className="mt-0.5 text-sm text-text">{value}</div>
    </div>
  );
}
